import React, { useState } from "react";
import EmojiPicker from "emoji-picker-react";
import { FaPlus, FaPaperPlane } from "react-icons/fa";
import PropTypes from "prop-types";
import useIsMobile from "hooks/useIsMobile";

const STYLES = `
  /* Input bar wrapper */
  .ciwe-bar {
    position: relative;
    display: flex;
    align-items: center;
    background: #101827;
    border: 1px solid #1e2d4a;
    border-radius: 12px;
    padding: 5px 6px;
    gap: 4px;
    transition: border-color 0.15s;
  }

  .ciwe-bar:focus-within { border-color: #22d3ee; }

  /* Emoji toggle button */
  .ciwe-emoji-btn {
    all: unset;
    box-sizing: border-box;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #8b9dc3;
    background: #162035;
    border: 1px solid #1e2d4a;
    font-size: 14px;
    flex-shrink: 0;
    transition: background 0.14s, color 0.14s;
  }

  .ciwe-emoji-btn:hover { background: #1e2d4a; color: #c4d0ea; }

  /* Text area */
  .ciwe-textarea {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    padding: 8px 6px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13.5px;
    color: #dbe4f8;
    line-height: 1.5;
    caret-color: #22d3ee;
    min-height: 36px;
    max-height: 100px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #1e2d4a transparent;
  }

  .ciwe-textarea::placeholder { color: #4a5f82; }

  /* Send button */
  .ciwe-send-btn {
    all: unset;
    box-sizing: border-box;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    flex-shrink: 0;
    background: rgba(34, 211, 238, 0.12);
    color: #22d3ee;
    border: 1px solid rgba(34, 211, 238, 0.22);
    transition: background 0.14s, filter 0.14s;
  }

  .ciwe-send-btn:hover { background: rgba(34, 211, 238, 0.20); filter: brightness(1.1); }

  .ciwe-send-btn:disabled {
    opacity: 0.28;
    cursor: not-allowed;
  }

  /* Emoji picker anchored above the bar */
  .ciwe-picker {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    z-index: 1000;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  }

  /* Override emoji-picker-react theme to dark */
  .ciwe-picker .EmojiPickerReact {
    --epr-bg-color: #0f1629 !important;
    --epr-category-label-bg-color: #0b1120 !important;
    --epr-text-color: #dbe4f8 !important;
    --epr-hover-bg-color: rgba(34,211,238,0.10) !important;
    --epr-search-border-color: #1e2d4a !important;
    --epr-search-input-bg-color: #162035 !important;
    --epr-search-input-text-color: #dbe4f8 !important;
    --epr-border-color: #1e2d4a !important;
    border: 1px solid #1e2d4a !important;
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const ChatInputWithEmojis = ({ onSendMessage, setInputText, inputText }) => {
  inject();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const isMobile = useIsMobile();

  const handleEmojiClick = (emojiData) => {
    setInputText((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputText.trim()) {
      onSendMessage(inputText);
      setInputText("");
    }
  };

  return (
    <div
      className="ciwe-bar"
      style={{ margin: isMobile ? "0" : "0 12px" }}
    >
      {/* Emoji toggle */}
      <button
        type="button"
        className="ciwe-emoji-btn"
        onClick={() => setShowEmojiPicker((v) => !v)}
        title="Emoji"
      >
        <FaPlus />
      </button>

      {/* Emoji picker */}
      {showEmojiPicker && (
        <div className="ciwe-picker">
          <EmojiPicker onEmojiClick={handleEmojiClick} theme="dark" />
        </div>
      )}

      {/* Input + send */}
      <form
        onSubmit={handleSubmit}
        style={{ flex: 1, display: "flex", alignItems: "center", minWidth: 0 }}
      >
        <textarea
          rows={1}
          className="ciwe-textarea"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder="Type a message..."
        />
        <button
          type="submit"
          className="ciwe-send-btn"
          disabled={!inputText.trim()}
          title="Send"
        >
          <FaPaperPlane />
        </button>
      </form>
    </div>
  );
};

ChatInputWithEmojis.propTypes = {
  onSendMessage: PropTypes.any.isRequired,
  setInputText: PropTypes.any.isRequired,
  inputText: PropTypes.string.isRequired,
};

export default ChatInputWithEmojis;